/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useReducer } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Question {
  id: string;
  text: string;
  options: string[];
  correct: number;
  difficulty?: string;
  subject?: string;
  chapter?: string;
}

interface Answer {
  qi: number;
  question: string;
  options: string[];
  subject: string;
  chapter: string;
  difficulty: string;
  selected: number | null;
  correct: number;
  isCorrect: boolean;
  skipped: boolean;
  timeSec: number;
}

interface AssessmentState {
  exam: string | null;
  userName: string;
  userClass: string;
  subjects: string[];
  selectedChapters: string[];
  durationMin: number;
  maxQuestions: number;
  questionBank: Question[];
  currentQ: number;
  answers: Answer[];
  level: number;
  secondsLeft: number;
  startTime: number | null;
  assessmentId: string | null;
  phone: string;
  email: string;
  assessmentType: string;
}

type Action =
  | { type: "SET_EXAM"; payload: string }
  | {
      type: "SET_USER";
      payload: {
        name: string;
        userClass: string;
        phone?: string;
        email?: string;
      };
    }
  | { type: "SET_SUBJECTS"; payload: string[] }
  | { type: "SET_SELECTED_CHAPTERS"; payload: string[] }
  | { type: "SET_DURATION"; payload: number }
  | { type: "SET_MAX_QUESTIONS"; payload: number }
  | { type: "SET_QUESTIONS"; payload: Question[] }
  | { type: "ADD_QUESTION"; payload: Question }
  | {
      type: "RECORD_ANSWER";
      payload: {
        qi: number;
        selected: number | null;
        timeSec: number;
        isCorrect?: boolean;
        subject?: string;
        chapter?: string;
        updateGlobalLevel?: number;
      };
    }
  | { type: "UPDATE_LEVEL"; payload: number }
  | { type: "NEXT_QUESTION" }
  | { type: "GOTO_QUESTION"; payload: number }
  | { type: "SET_ID"; payload: string }
  | { type: "RESTORE_ANSWERS"; payload: Answer[] }
  | { type: "LOAD_ASSESSMENT"; payload: Partial<AssessmentState> }
  | { type: "RESET" };

const initialState: AssessmentState = {
  exam: null,
  userName: "",
  userClass: "12",
  subjects: [],
  selectedChapters: [],
  durationMin: 12,
  maxQuestions: 10,
  questionBank: [],
  currentQ: 0,
  answers: [],
  level: 50,
  secondsLeft: 720,
  startTime: null,
  assessmentId: null,
  phone: "",
  email: "",
  assessmentType: "diagnostic",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function makeAnswerSlot(question: Question, qi: number): Answer {
  return {
    qi,
    question: question.text,
    options: question.options,
    subject: question.subject || "",
    chapter: question.chapter || "",
    difficulty: question.difficulty || "medium",
    selected: null,
    correct: question.correct,
    isCorrect: false,
    skipped: false,
    timeSec: 0,
  };
}

function recalcLevel(answers: Answer[]): number {
  const attempted = answers.filter((a) => !a.skipped && a.selected !== null);
  if (attempted.length === 0) return 50;
  const correct = attempted.filter((a) => a.isCorrect).length;
  return Math.round((correct / attempted.length) * 100);
}

// ─── Helper to get default class based on exam ──────────────────────────────
function getDefaultClassForExam(exam: string): string {
  if (exam === "IIT_NEET_FOUNDATION") {
    return "6";
  }
  return "11";
}

// ─── Reducer ──────────────────────────────────────────────────────────────────
function reducer(state: AssessmentState, action: Action): AssessmentState {
  switch (action.type) {
    case "SET_EXAM":
      const defaultClass = getDefaultClassForExam(action.payload);
      return {
        ...state,
        exam: action.payload,
        userClass: defaultClass,
        subjects: [],
        selectedChapters: [],
        questionBank: [],
        answers: [],
        currentQ: 0,
        level: 50,
      };

    case "SET_USER":
      return {
        ...state,
        userName: action.payload.name,
        userClass: action.payload.userClass,
        ...(action.payload.phone && { phone: action.payload.phone }),
        ...(action.payload.email && { email: action.payload.email }),
      };

    case "SET_SUBJECTS":
      return { ...state, subjects: action.payload };

    case "SET_SELECTED_CHAPTERS":
      return { ...state, selectedChapters: action.payload };

    case "SET_DURATION": {
      const min = Number(action.payload);
      let max = min <= 12 ? 10 : min <= 25 ? 18 : 28;

      if (state.exam === "IIT_NEET_FOUNDATION") {
        max = Math.min(max, 20);
      }

      return {
        ...state,
        durationMin: min,
        secondsLeft: min * 60,
        maxQuestions: max,
      };
    }

    case "SET_MAX_QUESTIONS":
      return {
        ...state,
        maxQuestions: action.payload,
      };

    case "SET_QUESTIONS":
      return {
        ...state,
        questionBank: action.payload,
        answers: action.payload.map((q, i) => makeAnswerSlot(q, i)),
        currentQ: 0,
        level: 50,
        startTime: Date.now(),
      };

    case "ADD_QUESTION": {
      const q = action.payload;
      const qi = state.questionBank.length;
      return {
        ...state,
        questionBank: [...state.questionBank, q],
        answers: [...state.answers, makeAnswerSlot(q, qi)],
      };
    }

    case "RECORD_ANSWER": {
      const { qi, selected, timeSec, customIsCorrect, subject, chapter } =
        action.payload as any;
      const newAnswers = [...state.answers];
      const question = state.questionBank[qi];

      if (!newAnswers[qi]) {
        // If answer slot doesn't exist, create it
        newAnswers[qi] = makeAnswerSlot(question, qi);
      }

      const skipped = selected === null || selected === undefined;
      const isCorrect =
        customIsCorrect !== undefined
          ? customIsCorrect
          : !skipped && selected === question.correct;

      // Store the full answer data
      newAnswers[qi] = {
        ...newAnswers[qi],
        question: question.text,
        options: question.options,
        subject: subject || question.subject || "",
        chapter: chapter || question.chapter || "",
        difficulty: question.difficulty || "medium",
        correct: question.correct,
        selected: skipped ? null : selected,
        isCorrect,
        skipped,
        timeSec: timeSec || 0,
      };

      const newLevel = recalcLevel(newAnswers);

      // Save to sessionStorage for recovery
      try {
        sessionStorage.setItem("ekluvya_answers", JSON.stringify(newAnswers));
        sessionStorage.setItem(
          "ekluvya_questionBank",
          JSON.stringify(state.questionBank),
        );
      } catch (_) {}

      return {
        ...state,
        answers: newAnswers,
        level: newLevel,
      };
    }

    case "UPDATE_LEVEL":
      return { ...state, level: action.payload };

    case "NEXT_QUESTION":
      return {
        ...state,
        currentQ: Math.min(state.currentQ + 1, state.questionBank.length - 1),
      };

    case "GOTO_QUESTION": {
      const idx = Number(action.payload);
      if (Number.isNaN(idx)) return state;
      return {
        ...state,
        currentQ: Math.min(Math.max(idx, 0), state.questionBank.length - 1),
      };
    }

    case "SET_ID":
      return { ...state, assessmentId: action.payload };

    case "RESTORE_ANSWERS":
      return { ...state, answers: action.payload };

    case "LOAD_ASSESSMENT":
      return {
        ...state,
        ...action.payload,
        // Ensure arrays are properly merged if needed
        questionBank: action.payload.questionBank || state.questionBank,
        answers: action.payload.answers || state.answers,
        selectedChapters:
          action.payload.selectedChapters || state.selectedChapters,
        subjects: action.payload.subjects || state.subjects,
      };

    case "RESET":
      // Clear sessionStorage on reset
      try {
        sessionStorage.removeItem("ekluvya_answers");
        sessionStorage.removeItem("ekluvya_questionBank");
      } catch (_) {}
      return { ...initialState };

    default:
      return state;
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────
const AssessmentContext = createContext<{
  state: AssessmentState;
  dispatch: React.Dispatch<Action>;
} | null>(null);

export function AssessmentProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Load saved state from sessionStorage on mount
  React.useEffect(() => {
    try {
      const savedAnswers = sessionStorage.getItem("ekluvya_answers");
      const savedQuestionBank = sessionStorage.getItem("ekluvya_questionBank");

      if (savedAnswers && savedQuestionBank && state.answers.length === 0) {
        const parsedAnswers = JSON.parse(savedAnswers);
        const parsedQuestionBank = JSON.parse(savedQuestionBank);

        if (
          Array.isArray(parsedAnswers) &&
          parsedAnswers.length > 0 &&
          Array.isArray(parsedQuestionBank) &&
          parsedQuestionBank.length > 0
        ) {
          dispatch({ type: "SET_QUESTIONS", payload: parsedQuestionBank });
          setTimeout(() => {
            dispatch({ type: "RESTORE_ANSWERS", payload: parsedAnswers });
          }, 0);
        }
      }
    } catch (_) {}
  }, []);

  return (
    <AssessmentContext.Provider value={{ state, dispatch }}>
      {children}
    </AssessmentContext.Provider>
  );
}

export function useAssessment() {
  const ctx = useContext(AssessmentContext);
  if (!ctx) {
    throw new Error("useAssessment must be used within AssessmentProvider");
  }
  return ctx;
}

export { getDefaultClassForExam };
export type { AssessmentState, Answer, Question, Action };
